package org.zq.astrock_ai.ui.research.gushi.data

import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.zq.astrock_ai.storage.HoldingsLocalStorage
import org.zq.astrock_ai.storage.QuantScanHistoryStorage
import org.zq.astrock_ai.storage.QuantScanPrefs
import org.zq.astrock_ai.storage.QuantScanPrefsStorage
import org.zq.astrock_ai.storage.ResearchKvStorage
import org.zq.astrock_ai.storage.WatchlistLocalStorage
import org.zq.astrock_ai.util.currentTimeMillis

@Serializable
data class ResearchBackupPayload(
    val version: Int = 1,
    val exportedAtMs: Long = 0L,
    val watchlist: String = "",
    val holdings: String = "",
    val paperTrades: String = "",
    val groupMap: String = "",
    val groupNames: String = "",
    val scanHistory: String = "",
    val scanPrefs: String = "",
    val layoutPrefs: String = "",
)

object ResearchBackup {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
        prettyPrint = true
    }

    fun exportText(): String {
        val prefs = QuantScanPrefsStorage.load()
        val payload = ResearchBackupPayload(
            exportedAtMs = currentTimeMillis(),
            watchlist = WatchlistLocalStorage.load().joinToString(","),
            holdings = HoldingsLocalStorage.load().orEmpty(),
            paperTrades = ResearchKvStorage.get("paper.trades.v1").orEmpty(),
            groupMap = ResearchKvStorage.get("watchlist.groups.v1").orEmpty(),
            groupNames = ResearchKvStorage.get("watchlist.groupNames.v1").orEmpty(),
            scanHistory = QuantScanHistoryStorage.load().orEmpty(),
            scanPrefs = "alert=${if (prefs.alertEnabled) 1 else 0};rescan=${prefs.autoRescanMinutes}",
            layoutPrefs = ResearchKvStorage.get("workbench.layout.v1").orEmpty(),
        )
        return json.encodeToString(payload)
    }

    fun importText(raw: String): Result<String> = runCatching {
        val payload = json.decodeFromString<ResearchBackupPayload>(raw.trim())
        require(payload.version in 1..2) { "不支持的备份版本" }
        if (payload.watchlist.isNotBlank()) {
            WatchlistLocalStorage.save(
                payload.watchlist.split(',')
                    .map { it.trim() }
                    .filter { it.length == 6 && it.all(Char::isDigit) }
                    .distinct(),
            )
        }
        if (payload.holdings.isNotBlank()) HoldingsLocalStorage.save(payload.holdings)
        if (payload.paperTrades.isNotBlank()) {
            ResearchKvStorage.put("paper.trades.v1", payload.paperTrades)
        }
        if (payload.groupMap.isNotBlank()) {
            ResearchKvStorage.put("watchlist.groups.v1", payload.groupMap)
        }
        if (payload.groupNames.isNotBlank()) {
            ResearchKvStorage.put("watchlist.groupNames.v1", payload.groupNames)
        }
        if (payload.scanHistory.isNotBlank()) QuantScanHistoryStorage.save(payload.scanHistory)
        if (payload.scanPrefs.isNotBlank()) {
            QuantScanPrefsStorage.save(parsePrefs(payload.scanPrefs))
        }
        if (payload.layoutPrefs.isNotBlank()) {
            ResearchKvStorage.put("workbench.layout.v1", payload.layoutPrefs)
        }
        "已导入备份（版本 ${payload.version}）"
    }

    private fun parsePrefs(raw: String): QuantScanPrefs {
        var alert = true
        var rescan = 0
        raw.split(';').forEach { part ->
            val idx = part.indexOf('=')
            if (idx <= 0) return@forEach
            when (part.substring(0, idx).trim()) {
                "alert" -> {
                    val v = part.substring(idx + 1).trim()
                    alert = v != "0" && !v.equals("false", ignoreCase = true)
                }
                "rescan" -> rescan = part.substring(idx + 1).trim().toIntOrNull()
                    ?.takeIf { it == 5 || it == 15 } ?: 0
            }
        }
        return QuantScanPrefs(alertEnabled = alert, autoRescanMinutes = rescan)
    }
}
